import React, { useState, useEffect } from 'react';
import { X, Upload, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAgendas } from '../../hooks/useAgendas';
import { db } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';

export default function UploadAgendaModal({ onClose }) {
  const { addAgenda, uploadAgendaAttachment } = useAgendas();
  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check extension
    const allowedExtensions = ['pdf', 'docx', 'doc'];
    const extension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      setErrorMessage("Only PDF and Word (.docx, .doc) files are allowed.");
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setErrorMessage('');
    if (!formData.title) {
      // Prefill title with filename without extension
      const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      setFormData(prev => ({ ...prev, title: baseName }));
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setErrorMessage("Please select a document to upload.");
      return;
    }

    setIsUploading(true);
    setErrorMessage('');
    try {
      // 1. Create the agenda document in Firestore with isHistorical flag
      const agendaId = await addAgenda({
        title: formData.title,
        date: formData.date,
        organizer: currentUser?.displayName || currentUser?.email?.split('@')[0] || 'System',
        isHistorical: true,
        attachments: []
      });

      // 2. Upload file to Firebase Storage
      const attachment = await uploadAgendaAttachment(agendaId, selectedFile);
      
      // Simulate virus scanner check
      const scannedAttachment = { ...attachment, virusFree: true };

      // 3. Update document with the completed attachment
      const docRef = doc(db, "agendas", agendaId);
      await updateDoc(docRef, {
        attachments: [scannedAttachment]
      });

      setUploadSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Historical upload failed:", err);
      setErrorMessage("Failed to upload historical agenda: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      backdropFilter: 'blur(4px)'
    }}>
      <div 
        className="card glass-card animate-scale-up" 
        style={{
          width: '500px',
          maxWidth: '90%',
          padding: 0,
          border: '1px solid var(--border-color)',
          backgroundColor: 'rgba(20, 24, 33, 0.98)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'rgba(10, 12, 18, 0.4)'
        }}>
          <h3 style={{ margin: 0, color: 'var(--primary)', fontSize: '1.10rem', fontWeight: '700' }}>
            Upload Historical Agendas
          </h3>
          <button 
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <div style={{ padding: '1.5rem' }}>
          {uploadSuccess ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <CheckCircle2 size={48} style={{ color: 'var(--primary)', marginBottom: '1rem', marginInline: 'auto' }} />
              <h4>Agenda Uploaded Successfully</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Adding to history log...</p>
            </div>
          ) : (
            <form onSubmit={handleUploadSubmit}>
              
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Meeting Date *</label>
                <input 
                  type="date"
                  required
                  name="date"
                  className="form-input"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">Meeting Topic / Title *</label>
                <input 
                  type="text"
                  required
                  name="title"
                  className="form-input"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. Q1 Board Meeting Agenda"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">Choose File (PDF, DOCX) *</label>
                <div style={{
                  border: '2px dashed var(--border-color)',
                  borderRadius: '4px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(0,0,0,0.1)'
                }} onClick={() => document.getElementById('historicalFile').click()}>
                  <Upload size={28} style={{ marginInline: 'auto', marginBottom: '0.5rem', color: 'var(--text-secondary)' }} />
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-primary)', display: 'block' }}>
                    {selectedFile ? selectedFile.name : "Click to select a file"}
                  </span>
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', display: 'block', marginTop: '0.25rem' }}>
                    Supports PDF, DOCX, DOC
                  </span>
                </div>
                <input 
                  type="file"
                  id="historicalFile"
                  accept=".pdf,.docx,.doc"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>

              {errorMessage && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  color: 'var(--danger)', 
                  fontSize: '0.78rem',
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  marginBottom: '1rem'
                }}>
                  <AlertCircle size={14} style={{ flexShrink: 0 }} />
                  <span>{errorMessage}</span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={onClose}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isUploading}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                >
                  {isUploading ? (
                    <><Loader2 className="spinner-icon" size={14} style={{ animation: 'spin 1s linear infinite' }} /> Uploading...</>
                  ) : 'Upload Document'}
                </button>
              </div>

            </form>
          )}
        </div>
      </div>
    </div>
  );
}
